/**
 * Device Parser Utility
 *
 * Parses user-agent strings into human-readable device descriptions.
 * Used for displaying active sessions in a user-friendly format.
 */

export interface DeviceInfo {
  browser: string;
  os: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  displayName: string;
}

/**
 * Parse a user-agent string into human-readable device info
 */
export function parseDevice(userAgent: string | null): DeviceInfo {
  if (!userAgent) {
    return {
      browser: 'Unknown',
      os: 'Unknown',
      deviceType: 'unknown',
      displayName: 'Unknown device',
    };
  }

  const browser = detectBrowser(userAgent);
  const os = detectOS(userAgent);
  const deviceType = detectDeviceType(userAgent);

  return {
    browser,
    os,
    deviceType,
    displayName: `${browser} on ${os}`,
  };
}

/**
 * Detect browser from user-agent
 */
function detectBrowser(ua: string): string {
  // Order matters - check more specific patterns first

  // Edge (Chromium-based, appears before Chrome in UA)
  if (ua.includes('Edg/') || ua.includes('Edge/')) {
    return 'Edge';
  }

  // Opera (also Chromium-based)
  if (ua.includes('OPR/') || ua.includes('Opera/')) {
    return 'Opera';
  }

  // Chrome (must check after Edge/Opera)
  if (ua.includes('Chrome/') && !ua.includes('Chromium/')) {
    return 'Chrome';
  }

  // Firefox
  if (ua.includes('Firefox/')) {
    return 'Firefox';
  }

  // Safari (must check after Chrome since Chrome also has Safari in UA)
  if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
    return 'Safari';
  }

  // Internet Explorer
  if (ua.includes('MSIE') || ua.includes('Trident/')) {
    return 'Internet Explorer';
  }

  // Mobile browsers
  if (ua.includes('SamsungBrowser/')) {
    return 'Samsung Internet';
  }

  return 'Unknown browser';
}

/**
 * Detect operating system from user-agent
 */
function detectOS(ua: string): string {
  // Mobile OS (check first as they may also include desktop-like strings)

  // iOS
  if (ua.includes('iPhone')) {
    return 'iPhone';
  }
  if (ua.includes('iPad')) {
    return 'iPad';
  }
  if (ua.includes('iPod')) {
    return 'iPod';
  }

  // Android
  if (ua.includes('Android')) {
    return 'Android';
  }

  // Desktop OS

  // Windows
  if (ua.includes('Windows NT 10.0')) {
    return 'Windows';
  }
  if (ua.includes('Windows NT 6.3')) {
    return 'Windows 8.1';
  }
  if (ua.includes('Windows NT 6.2')) {
    return 'Windows 8';
  }
  if (ua.includes('Windows NT 6.1')) {
    return 'Windows 7';
  }
  if (ua.includes('Windows')) {
    return 'Windows';
  }

  // macOS
  if (ua.includes('Mac OS X') || ua.includes('Macintosh')) {
    return 'Mac';
  }

  // Linux
  if (ua.includes('Linux') && !ua.includes('Android')) {
    return 'Linux';
  }

  // Chrome OS
  if (ua.includes('CrOS')) {
    return 'Chrome OS';
  }

  return 'Unknown OS';
}

/**
 * Detect device type from user-agent
 */
function detectDeviceType(ua: string): 'desktop' | 'mobile' | 'tablet' | 'unknown' {
  // Tablets
  if (ua.includes('iPad') || (ua.includes('Android') && !ua.includes('Mobile'))) {
    return 'tablet';
  }

  // Mobile devices
  if (
    ua.includes('iPhone') ||
    ua.includes('iPod') ||
    (ua.includes('Android') && ua.includes('Mobile')) ||
    ua.includes('Mobile') ||
    ua.includes('webOS') ||
    ua.includes('BlackBerry') ||
    ua.includes('Opera Mini') ||
    ua.includes('IEMobile')
  ) {
    return 'mobile';
  }

  // Desktop (Windows, Mac, Linux without mobile indicators)
  if (
    ua.includes('Windows') ||
    ua.includes('Macintosh') ||
    ua.includes('Mac OS X') ||
    (ua.includes('Linux') && !ua.includes('Android')) ||
    ua.includes('CrOS')
  ) {
    return 'desktop';
  }

  return 'unknown';
}

/**
 * Format a date as relative time (e.g., "Just now", "2 hours ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  // For older dates, show the actual date
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}
